import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Form, Input, Select, Switch,
  Button, Tabs, Spin, notification,
  Row, Col, List,
  Empty, Avatar, Typography, Card, Alert, Space
} from 'antd';
import {
  TeamOutlined,
  LockOutlined,
  CreditCardOutlined,
  SendOutlined,
  CopyOutlined
} from '@ant-design/icons';
import userService from '../../../../services/userService';
import agencyCustomerService from '../../../../services/agencyCustomerService';
import userRolePermissionService from '../../../../services/userRolePermissionService';
import UserRolesPermissions from '../UserRolesPermissions';
import AgencyCustomersTab from '../AgencyCustomersTab';
import SchedoxSyncStatus from '../../../sync/SchedoxSyncStatus';
import xeroIntegrationService from '../../../../services/xeroIntegrationService';

const { Option } = Select;
const { TabPane } = Tabs;
const { Text } = Typography;

const paymentFieldNames = [
  'payment_method_type',
  'payment_timing',
  'selected_stripe_account_type'
];

const getRoleNames = (user) => (
  Array.isArray(user?.roles)
    ? user.roles.map(role => (typeof role === 'string' ? role : role?.name)).filter(Boolean)
    : []
);

const isAgencyUser = (user) => {
  const roleNames = getRoleNames(user).map(roleName => String(roleName).toLowerCase());

  if (roleNames.includes('agency')) {
    return true;
  }

  if (roleNames.some(roleName => ['admin', 'superadmin', 'subadmin', 'developer'].includes(roleName))) {
    return false;
  }

  return String(user?.user_type) === '1';
};

/**
 * Edit User Modal Component
 * Updated to handle user roles and agency-customer relationships
 */
const EditModal = ({ visible, userId, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [propertyRanges, setPropertyRanges] = useState([]);
  const [managedCustomers, setManagedCustomers] = useState([]);
  const [managingAgencies] = useState([]);
  const [loadingRelationships] = useState(false);
  const [paymentProfile, setPaymentProfile] = useState(null);
  const [paymentSetupUrls, setPaymentSetupUrls] = useState({});
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedStripeAccountType, setSelectedStripeAccountType] = useState('stripe_1');
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [xeroAccountsLoading, setXeroAccountsLoading] = useState(false);
  const [accountType, setAccountType] = useState('private');
  const loadSchedoxSync = useCallback(() => userService.getSchedoxSyncStatus(userId), [userId]);
  const startSchedoxSync = useCallback(() => userService.startSchedoxSync(userId), [userId]);
  
  // Fetch user data and property ranges when modal opens
  useEffect(() => {
    if (visible && userId) {
      fetchUserData();
      fetchPropertyRanges();
      fetchPaymentProfile();
      fetchXeroAccounts();
    }
  }, [visible, userId]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const [response, rolesResponse] = await Promise.all([
        userService.getUserById(userId),
        userRolePermissionService.getUserRoles(userId).catch(() => null)
      ]);
      const userWithRoles = {
        ...response,
        roles: response.roles || rolesResponse?.data?.roles || []
      };
      setUser(userWithRoles);
      const resolvedAccountType = response.account_type || (response.company_name ? 'company' : 'private');
      setAccountType(resolvedAccountType);
      
      // Set form values
      form.setFieldsValue({
        first_name: response.first_name,
        last_name: response.last_name,
        email: response.email,
        phone: response.phone,
        status: response.status === '1' || response.status === 1,
        user_type: response.user_type?.toString() || '0',
        account_type: resolvedAccountType,
        service_type: response.service_type || 'LinenHire',
        property_range_id: response.property_range_id,
        company_name: response.company_name,
        company_registration: response.company_registration,
        apartment_number: response.apartment_number,
        address: response.address,
        city: response.city,
        post_code: response.post_code,
        country: response.country || 'GB',
        secondary_email: response.secondary_email,
        sync_to_cleaning: Boolean(response.sync_to_cleaning),
        xero_account_id: response.xero_account_id || undefined
      });

    } catch (error) {
      notification.error({
        message: 'Error loading user',
        description: error.message || 'Failed to load user data'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchXeroAccounts = async () => {
    try {
      setXeroAccountsLoading(true);
      const response = await xeroIntegrationService.listAccounts();
      setXeroAccounts((response?.data || []).filter(
        account => account.status === 'connected' && account.is_active
      ));
    } catch (error) {
      notification.error({
        message: 'Error loading Xero accounts',
        description: error.message || 'Failed to load Xero account choices'
      });
    } finally {
      setXeroAccountsLoading(false);
    }
  };
  
  const fetchPropertyRanges = async () => {
    try {
      const response = await userService.getPropertyRanges();
      setPropertyRanges(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching property ranges',
        description: error.message || 'Failed to load property ranges'
      });
    }
  };

  const fetchPaymentProfile = async () => {
    try {
      setPaymentLoading(true);
      const response = await userService.getPaymentProfile(userId);
      const profile = response.data || response;
      const activeAccounts = profile.stripe_accounts || [];
      const selectedAccountType = profile.selected_stripe_account_type
        || profile.active_stripe_account_type
        || activeAccounts[0]?.account_type
        || 'stripe_1';
      setPaymentProfile(profile);
      setPaymentSetupUrls({});
      setSelectedStripeAccountType(selectedAccountType);
      form.setFieldsValue({
        payment_method_type: profile.payment_method_type || 'invoice',
        payment_timing: profile.payment_timing || 'pay_later',
        selected_stripe_account_type: selectedAccountType
      });
    } catch (error) {
      notification.error({
        message: 'Error loading payment profile',
        description: error.message || 'Failed to load payment profile'
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // Update name field as combination of first and last name
      values.name = `${values.first_name} ${values.last_name}`;
      
      // Convert boolean status to number
      values.status = values.status ? 1 : 0;
      paymentFieldNames.forEach((fieldName) => delete values[fieldName]);

      // If this is an agency and there are managed customers, update relationships
      if (isAgency && managedCustomers.length > 0) {
        try {
          // This would need to be implemented in your agencyCustomerService
          const customerIds = managedCustomers.map(customer => customer?.id || customer);
          await agencyCustomerService.updateAgencyCustomers(userId, customerIds);
        } catch (relError) {
          console.error('Error updating agency-customer relationships:', relError);
        }
      }
      
      // Call API to update user
      await userService.updateUser(userId, values);
      
      notification.success({
        message: 'User updated successfully',
        description: 'The user information has been updated.'
      });
      
      onSuccess();
      
    } catch (error) {
      notification.error({
        message: 'Failed to update user',
        description: error.message || 'Please check your inputs and try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if user is an agency
  const isAgency = isAgencyUser(user);

  // Handle updates to customer assignments from AgencyCustomersTab
  const handleCustomersUpdated = (customers) => {
    setManagedCustomers(customers);
  };

  const handlePaymentProfileSave = async () => {
    try {
      const values = form.getFieldsValue(paymentFieldNames);
      setPaymentLoading(true);
      const response = await userService.updatePaymentProfile(userId, values);
      const profile = response.data || response;
      setPaymentProfile(profile);
      setSelectedStripeAccountType(profile.selected_stripe_account_type || selectedStripeAccountType);
      notification.success({
        message: 'Payment profile saved'
      });
    } catch (error) {
      notification.error({
        message: 'Payment profile not saved',
        description: error.message || 'Please check Stripe settings.'
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleCreatePaymentSetupLink = async (accountType = selectedStripeAccountType) => {
    try {
      setPaymentLoading(true);
      const response = await userService.createPaymentSetupLink(userId, true, accountType);
      setPaymentSetupUrls(prev => ({
        ...prev,
        [accountType]: response.setup_url || ''
      }));
      if (response.data) {
        setPaymentProfile(response.data);
      }
      if (response.email_error) {
        notification.warning({
          message: 'Setup link created',
          description: 'Email was not sent. Copy the secure Stripe setup link below.'
        });
      } else {
        notification.success({
          message: 'Setup email sent',
          description: 'The secure Stripe setup link was sent to the client.'
        });
      }
    } catch (error) {
      notification.error({
        message: 'Setup link failed',
        description: error.message || 'Stripe setup link could not be created.'
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleCopySetupLink = async (accountType = 'stripe_1') => {
    if (!paymentSetupUrls[accountType]) return;
    await navigator.clipboard.writeText(paymentSetupUrls[accountType]);
    notification.success({ message: 'Setup link copied' });
  };

  const stripeAccounts = paymentProfile?.stripe_accounts || [];
  const selectedStripeAccount = stripeAccounts.find(
    account => account.account_type === selectedStripeAccountType
  ) || stripeAccounts[0];

  return (
    <Modal
      title="Edit User"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          className="bg-golden-navy"
        >
          Update User
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        {user ? (
          <Form
            form={form}
            layout="vertical"
          >
            <Tabs defaultActiveKey="basic">
              <TabPane tab="Basic Information" key="basic">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="account_type"
                      label="Account Type"
                      rules={[{ required: true, message: 'Please select an account type' }]}
                    >
                      <Select onChange={setAccountType}>
                        <Option value="private">Private</Option>
                        <Option value="company">Company</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="sync_to_cleaning"
                      label="Sync to Schedox"
                      valuePropName="checked"
                      extra="Turning this on or off applies the same setting to all existing properties. When on, individual properties can be switched off."
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                    <SchedoxSyncStatus
                      loadStatus={loadSchedoxSync}
                      startSync={startSchedoxSync}
                    />
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="first_name"
                      label="First Name"
                      rules={[
                        { required: true, message: 'Please enter first name' },
                        { pattern: /^[a-zA-Z ]*$/, message: 'Name must only contain letters' }
                      ]}
                    >
                      <Input placeholder="Enter first name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="last_name"
                      label="Last Name"
                      rules={[
                        { required: true, message: 'Please enter last name' },
                        { pattern: /^[a-zA-Z ]*$/, message: 'Name must only contain letters' }
                      ]}
                    >
                      <Input placeholder="Enter last name" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Please enter email' },
                        { type: 'email', message: 'Please enter a valid email' }
                      ]}
                    >
                      <Input placeholder="Enter email address" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="secondary_email"
                      label="Secondary Email"
                      rules={[
                        { type: 'email', message: 'Please enter a valid email' }
                      ]}
                    >
                      <Input placeholder="Enter secondary email address" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[
                        { required: true, message: 'Please enter phone number' },
                        { max: 20, message: 'Phone number must be less than 20 characters' }
                      ]}
                    >
                      <Input placeholder="Enter phone number" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="status"
                      label="Status"
                      valuePropName="checked"
                    >
                      <Switch 
                        checkedChildren="Active" 
                        unCheckedChildren="Inactive" 
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="service_type"
                      label="Service Type"
                      rules={[{ required: true, message: 'Please select service type' }]}
                    >
                      <Select placeholder="Select service type">
                        <Option value="LinenHire">Linen Hire</Option>
                        <Option value="BabyEquipment">Baby Equipment</Option>
                        <Option value="CleaningService">Cleaning Service</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="property_range_id"
                      label="Property Range"
                      rules={[{ required: true, message: 'Please select property range' }]}
                    >
                      <Select placeholder="Select property range">
                        {propertyRanges.map(range => (
                          <Option key={range.id} value={range.id}>{range.range}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="password"
                      label="Password (Leave empty to keep current)"
                    >
                      <Input.Password placeholder="Enter new password" />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              
              {accountType === 'company' && <TabPane tab="Company Information" key="company">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="company_name"
                      label="Company Name"
                      rules={[{ required: true, message: 'Please enter company name' }]}
                    >
                      <Input placeholder="Enter company name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="company_registration"
                      label="Company Registration Number"
                    >
                      <Input placeholder="Enter company registration number" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="xero_account_id"
                      label="Default Xero account"
                      extra="Invoices default to this account. Leave empty to choose the system default in the invoice popup."
                    >
                      <Select
                        allowClear
                        loading={xeroAccountsLoading}
                        placeholder="No client-specific Xero account"
                      >
                        {xeroAccounts.map(account => (
                          <Option key={account.id} value={account.id}>
                            {account.display_name || account.xero_tenant_name}
                            {account.oauth_app === 'xero2' ? ' (Linen)' : ' (Golden Group)'}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>}
              
              <TabPane tab="Address Information" key="address">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="apartment_number"
                      label="Apartment/Building Number"
                      rules={[{ required: true, message: 'Please enter apartment/building number' }]}
                    >
                      <Input placeholder="Enter apartment/building number" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="address"
                      label="Address"
                      rules={[{ required: true, message: 'Please enter address' }]}
                    >
                      <Input placeholder="Enter address" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="city"
                      label="City"
                      rules={[{ required: true, message: 'Please enter city' }]}
                    >
                      <Input placeholder="Enter city" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="post_code"
                      label="Post Code"
                      rules={[{ required: true, message: 'Please enter post code' }]}
                    >
                      <Input placeholder="Enter post code" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="country"
                      label="Country"
                      rules={[{ required: true, message: 'Please select country' }]}
                    >
                      <Select placeholder="Select country">
                        <Option value="GB">United Kingdom</Option>
                        <Option value="US">United States</Option>
                        <Option value="FR">France</Option>
                        <Option value="DE">Germany</Option>
                        <Option value="ES">Spain</Option>
                        <Option value="IT">Italy</Option>
                        <Option value="NL">Netherlands</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>

              <TabPane
                tab={<span><CreditCardOutlined /> Payment Details</span>}
                key="payment-details"
              >
                <Spin spinning={paymentLoading}>
                  <Card size="small" title="Payment settings for this user" className="mb-4">
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="payment_method_type" label="Payment Method">
                          <Select>
                            <Option value="invoice">Invoice</Option>
                            <Option value="stripe">Stripe</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="payment_timing" label="Payment Timing">
                          <Select>
                            <Option value="pay_later">Pay Later</Option>
                            <Option value="pay_now">Pay Now</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="selected_stripe_account_type" label="Active Stripe Account">
                          <Select
                            onChange={value => setSelectedStripeAccountType(value)}
                            placeholder="Select a Stripe account"
                          >
                            {stripeAccounts.map(account => (
                              <Option key={account.account_type} value={account.account_type}>
                                {account.display_name || account.account_label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  {selectedStripeAccount ? (
                    <Card
                      size="small"
                      title={selectedStripeAccount.display_name || selectedStripeAccount.account_label}
                      className="mb-4"
                    >
                      <Alert
                        type={selectedStripeAccount.has_saved_card ? 'success' : 'warning'}
                        showIcon
                        className="mb-4"
                        message={
                          selectedStripeAccount.has_saved_card
                            ? `Saved card: ${selectedStripeAccount.stripe_card_brand || 'card'} ending ${selectedStripeAccount.stripe_card_last4 || ''}`
                            : 'No saved card for this user'
                        }
                        description={
                          selectedStripeAccount.stripe_last_error
                            ? `Last Stripe error: ${selectedStripeAccount.stripe_last_error}`
                            : 'Stripe account credentials are managed once in System Settings. Use the setup link to save this user’s card.'
                        }
                      />

                      {selectedStripeAccount.has_saved_card ? (
                        <div className="text-sm mb-4">
                          <div><strong>Brand:</strong> {selectedStripeAccount.stripe_card_brand || 'N/A'}</div>
                          <div><strong>Last 4:</strong> {selectedStripeAccount.stripe_card_last4 || 'N/A'}</div>
                          <div>
                            <strong>Expires:</strong>{' '}
                            {selectedStripeAccount.stripe_card_exp_month || '--'}/{selectedStripeAccount.stripe_card_exp_year || '----'}
                          </div>
                        </div>
                      ) : null}

                      {paymentSetupUrls[selectedStripeAccount.account_type] ? (
                        <Input.Group compact className="mb-4">
                          <Input
                            style={{ width: 'calc(100% - 96px)' }}
                            value={paymentSetupUrls[selectedStripeAccount.account_type]}
                            readOnly
                          />
                          <Button
                            icon={<CopyOutlined />}
                            onClick={() => handleCopySetupLink(selectedStripeAccount.account_type)}
                          >
                            Copy
                          </Button>
                        </Input.Group>
                      ) : null}

                      <Button
                        icon={<SendOutlined />}
                        onClick={() => handleCreatePaymentSetupLink(selectedStripeAccount.account_type)}
                      >
                        Create Setup Link
                      </Button>
                    </Card>
                  ) : (
                    <Alert
                      className="mb-4"
                      type="warning"
                      showIcon
                      message="No active Stripe account"
                      description="Configure Stripe accounts in System Settings → Stripe Settings."
                    />
                  )}

                  <Space>
                    <Button type="primary" className="bg-golden-navy" onClick={handlePaymentProfileSave}>
                      Save Payment Settings
                    </Button>
                  </Space>
                </Spin>
              </TabPane>

              {/* Roles & Permissions Tab */}
              <TabPane 
                tab={<span><LockOutlined /> Roles & Permissions</span>}
                key="roles-permissions"
              >
                <UserRolesPermissions 
                  userId={userId}
                  userName={user.name || `${user.first_name || ''} ${user.last_name || ''}`}
                />
              </TabPane>
              
              {/* Agency Customers Tab - Only show if user is an agency */}
              {isAgency && (
                <TabPane 
                  tab={<span><TeamOutlined /> Managed Customers</span>}
                  key="managed-customers"
                >
                  <AgencyCustomersTab 
                    agencyId={userId} 
                    onCustomersUpdated={handleCustomersUpdated}
                  />
                </TabPane>
              )}

              {/* Managing Agencies Tab - Only show if user is a customer */}
              {!isAgency && managingAgencies.length > 0 && (
                <TabPane 
                  tab={<span><TeamOutlined /> Managing Agencies</span>}
                  key="managing-agencies"
                >
                  <Card>
                    <Spin spinning={loadingRelationships}>
                      {managingAgencies.length > 0 ? (
                        <List
                          itemLayout="horizontal"
                          dataSource={managingAgencies}
                          renderItem={item => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<Avatar icon={<TeamOutlined />} />}
                                title={<Text strong>{item.name}</Text>}
                                description={
                                  <div>
                                    <div>{item.email}</div>
                                    {item.phone && <div>{item.phone}</div>}
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Empty description="No agencies are managing this customer" />
                      )}
                    </Spin>
                  </Card>
                </TabPane>
              )}
            </Tabs>
          </Form>
        ) : (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
            <p className="ml-3 text-gray-500">Loading user data...</p>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default EditModal;
